// aifab - CloneRunModal
// Compact redesign: label + priority inline, params in a 2-column grid.

import { useState } from 'react';
import { apiFetch } from '../../hooks/apiFetch';
import { Modal, Err, Spinner, ModalBody, ModalFooter } from '../common';

export function CloneRunModal({ run, sourceDetail, onClose, onCloned }) {
  const originalParams = sourceDetail?.params_snapshot || {};

  const [label,    setLabel]    = useState((run.run_label || '') + ' (clone)');
  const [priority, setPriority] = useState(run.priority || 'NORMAL');
  const [params,   setParams]   = useState(
    Object.fromEntries(Object.entries(originalParams).filter(([k]) => !k.startsWith('_')))
  );
  const [saving,  setSaving] = useState(false);
  const [err,     setErr]    = useState(null);

  const setParam = (key, val) => setParams(p => ({ ...p, [key]: val }));
  const resetParam = (key) => setParam(key, originalParams[key]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const overrides = {};
      for (const [k, v] of Object.entries(params)) {
        if (String(v) !== String(originalParams[k] ?? '')) overrides[k] = String(v);
      }
      const cloned = await apiFetch(`/runs/${run.id}/clone`, {
        method: 'POST',
        body: JSON.stringify({
          run_label:       label || null,
          priority,
          param_overrides: Object.keys(overrides).length > 0 ? overrides : null,
        }),
      });
      onCloned(cloned);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const paramEntries = Object.entries(params).filter(([k]) => !k.startsWith('_'));
  const changedCount  = paramEntries.filter(([k, v]) => String(v) !== String(originalParams[k] ?? '')).length;

  return (
    <Modal title={`Clone Run #${run.run_number}`} onClose={onClose}>
      <ModalBody>
      <Err msg={err} />

      {/* Label + Priority side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10, marginBottom: 16 }}>
        <div className="form-grp" style={{ margin: 0 }}>
          <label className="form-lbl">Run label</label>
          <input className="form-inp" value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="e.g. experiment-2, higher-lr" />
        </div>
        <div className="form-grp" style={{ margin: 0 }}>
          <label className="form-lbl">Priority</label>
          <select className="form-inp" value={priority} onChange={e => setPriority(e.target.value)}>
            <option>LOW</option>
            <option>NORMAL</option>
            <option>HIGH</option>
          </select>
        </div>
      </div>

      {/* Hyperparameters - compact 2-column grid */}
      {paramEntries.length > 0 && (
        <div className="form-grp" style={{ margin: 0 }}>
          <div className="flex-btw mb8">
            <label className="form-lbl" style={{ margin: 0 }}>Hyperparameters</label>
            {changedCount > 0
              ? <span className="badge b-amber">{changedCount} modified</span>
              : <span className="xs c-sl">unchanged</span>
            }
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '6px 12px',
            background: '#F1F5FB',
            border: '1px solid var(--bd)',
            borderRadius: '4px',
            padding: '10px 12px',
            maxHeight: 260,
            overflowY: 'auto',
          }}>
            {paramEntries.map(([key, val]) => {
              const changed = String(val) !== String(originalParams[key] ?? '');
              return (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div className="mono" style={{ fontSize: 10, color: changed ? 'var(--or)' : 'var(--sl)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{key}</span>
                    {changed && (
                      <button className="btn btn-ghost btn-xs" style={{ padding: '0 4px', height: 14, fontSize: 10 }}
                        title="Reset" onClick={() => resetParam(key)}>↺</button>
                    )}
                  </div>
                  <input
                    className="form-inp"
                    style={{
                      padding: '4px 7px', fontSize: 12,
                      background: changed ? 'rgba(232,71,10,.06)' : undefined,
                      borderColor: changed ? 'var(--or)' : undefined,
                    }}
                    value={val}
                    onChange={e => setParam(key, e.target.value)}
                  />
                </div>
              );
            })}
          </div>
          {changedCount > 0 && (
            <div className="form-hint">Orange = changed from original. Click ↺ to reset individual values.</div>
          )}
        </div>
      )}

      {paramEntries.length === 0 && (
        <div className="form-hint" style={{ marginBottom: 8 }}>
          No hyperparameters in original run. Clone will inherit all settings from Run #{run.run_number}.
        </div>
      )}

      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <Spinner size={12} /> : 'Clone & Create Run'}
        </button>
      </ModalFooter>
    </Modal>
  );
}
